import { getLinkedinAccountById, triggerAccountVerification } from 'API/linkedin-accounts';
import { LinkedinAccountStatuses, LinkedinAccountSyncStages, normalizeAccountStatus } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import useErrorStore from 'stores/error-store';

const useCheckLinkedinAccountStatus = () => {
  useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const setTab = useAppBarNavigationStore((state) => state.setActiveTab);

  const [autoSyncLoading, setAutoSyncLoading] = useState(false);

  const [autoSyncProcessingDialog, setAutoSyncProcessingDialog] = useState<{ open: boolean; syncStage: LinkedinAccountSyncStages | null }>({
    open: false,
    syncStage: null,
  });
  const [autoSyncSuccessful, setAutoSyncSuccessful] = useState<boolean>(false);
  const [autoSyncWarningDialog, setAutoSyncWarningDialog] = useState<{
    open: boolean;
    status: LinkedinAccountStatuses | null;
    accountId: string | null;
  }>({
    open: false,
    status: null,
    accountId: null,
  });

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const checkInterval = 1 * 1000;

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (!_.isNil(intervalIdRef.current)) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, []);

  /**
   * General handlers
   */

  const checkAccountStatus = async (accountId: string) => {
    const res = await getLinkedinAccountById(accountId);

    if (!res.error) {
      if (!_.isNil(intervalIdRef.current)) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      setAutoSyncProcessingDialog((prev) => ({ ...prev, syncStage: res.syncStage }));

      switch (normalizeAccountStatus(res.status)) {
        case LinkedinAccountStatuses.PENDING:
          intervalIdRef.current = setInterval(() => checkAccountStatus(accountId), checkInterval);
          break;
        case LinkedinAccountStatuses.SYNCHRONIZED:
          stopCheckAccountStatusInterval();
          handleCloseAutoSyncProcessingDialog();
          setAutoSyncSuccessful(true);
          break;
        case LinkedinAccountStatuses.NOT_SYNCHRONIZED:
        case LinkedinAccountStatuses.WRONG_CREDENTIALS:
        case LinkedinAccountStatuses.TWO_FA:
        case LinkedinAccountStatuses.WRONG_PIN:
        case LinkedinAccountStatuses.CAPTCHA:
        case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
        case LinkedinAccountStatuses.CHOOSE_CONTRACT:
        case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
        case LinkedinAccountStatuses.RECRUITER_PARALLEL_LOGIN:
        case LinkedinAccountStatuses.FAIR_USE_PAUSE:
        case LinkedinAccountStatuses.THIRD_PARTY:
        case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
        case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
          stopCheckAccountStatusInterval();
          handleCloseAutoSyncProcessingDialog();
          handleOpenAutoSyncWarningDialog(res.status, accountId);
          break;
        default:
          stopCheckAccountStatusInterval();
          break;
      }
    } else {
      stopCheckAccountStatusInterval();
      handleCloseAutoSyncProcessingDialog();
      handleOpenErrorDialog(res?.error, '/linkedinAccounts/:id', 'get');
    }
  };

  const startCheckAccountStatusInterval = (accountId: string) => {
    // Clear any existing interval before starting a new one
    stopCheckAccountStatusInterval();
    setAutoSyncLoading(true);
    handleOpenAutoSyncProcessingDialog();
    intervalIdRef.current = setInterval(() => checkAccountStatus(accountId), checkInterval);
  };

  const stopCheckAccountStatusInterval = () => {
    setAutoSyncLoading(false);
    if (!_.isNil(intervalIdRef.current)) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  };

  const initSyncForLinkedinAccountById = async (accountId: string) => {
    if (!import.meta.env.DEV) {
      await triggerAccountVerification(accountId, 'high');
    }
  };

  /**
   * Auto sync processing dialog handlers
   */

  const handleOpenAutoSyncProcessingDialog = () => {
    setAutoSyncProcessingDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseAutoSyncProcessingDialog = () => {
    setAutoSyncProcessingDialog({ open: false, syncStage: null });
  };

  /**
   * Auto sync successful snackbar handlers
   */

  const handleCloseAutoSyncSuccessfulSnackbar = () => {
    setAutoSyncSuccessful(false);
  };

  /**
   * Auto sync warning dialog handlers
   */

  const handleOpenAutoSyncWarningDialog = (status: LinkedinAccountStatuses, accountId: string) => {
    setAutoSyncWarningDialog({ open: true, status, accountId });
  };

  const handleCloseAutoSyncWarningDialog = () => {
    setAutoSyncWarningDialog({ open: false, status: null, accountId: null });
    setTab(NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT);
  };

  return {
    autoSyncLoading,
    autoSyncProcessingDialog,
    autoSyncSuccessful,
    autoSyncWarningDialog,
    startCheckAccountStatusInterval,
    stopCheckAccountStatusInterval,
    initSyncForLinkedinAccountById,
    handleOpenAutoSyncProcessingDialog,
    handleCloseAutoSyncProcessingDialog,
    handleCloseAutoSyncSuccessfulSnackbar,
    handleCloseAutoSyncWarningDialog,
  };
};

export default useCheckLinkedinAccountStatus;
